import { useEffect, useRef, useState } from "react";
import { Instagram, ArrowUpRight } from "lucide-react";
import { useLang } from "./i18n";
import { useInView } from "./bits";

const SRC = "https://www.instagram.com/embed.js";
let loader = null;
const loadScript = () => {
  if (window.instgrm) return Promise.resolve(window.instgrm);
  if (!loader) {
    loader = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SRC; s.async = true;
      s.onload = () => (window.instgrm ? resolve(window.instgrm) : reject(new Error("instgrm missing")));
      s.onerror = () => { loader = null; reject(new Error("blocked")); };
      document.body.appendChild(s);
    });
  }
  return loader;
};

const Fallback = ({ url, i }) => {
  const { lang } = useLang();
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="group flex flex-col justify-between aspect-[4/5] rounded-[24px] bg-[#0A1128] text-white p-6 overflow-hidden relative" data-testid={`ig-fallback-${i}`}>
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#E8173A]" aria-hidden />
      <Instagram className="relative h-10 w-10" />
      <div className="relative">
        <div className="font-display text-2xl font-black">{lang === "en" ? "Watch on Instagram" : "Guarda su Instagram"}</div>
        <div className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-[#7CC6FA] group-hover:text-white">{url.includes("/reel/") ? "Reel" : "Post"}<ArrowUpRight className="h-4 w-4" /></div>
      </div>
    </a>
  );
};

const Embed = ({ url, i, failed }) => {
  const ref = useRef(null);
  const [fallback, setFallback] = useState(false);
  useEffect(() => {
    if (failed) { setFallback(true); return; }
    const fit = () => ref.current?.querySelectorAll("iframe").forEach((f) => {
      f.style.setProperty("width", "100%", "important");
      f.style.setProperty("min-width", "0", "important");
      f.style.setProperty("max-width", "100%", "important");
    });
    const mo = new MutationObserver(fit);
    if (ref.current) mo.observe(ref.current, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
    const t = setTimeout(() => { if (!ref.current?.querySelector("iframe.instagram-media-rendered")) setFallback(true); }, 9000);
    return () => { clearTimeout(t); mo.disconnect(); };
  }, [failed]);
  if (fallback) return <Fallback url={url} i={i} />;
  return (
    <div ref={ref} className="ig-embed rounded-[24px]" data-testid={`ig-embed-${i}`}>
      <blockquote className="instagram-media" data-instgrm-permalink={`${url}?utm_source=ig_embed&utm_campaign=loading`} data-instgrm-version="14"
        style={{ background: "#FFF", border: 0, borderRadius: 24, margin: 0, maxWidth: 540, minWidth: 0, width: "100%", padding: 0 }}>
        <a href={url} target="_blank" rel="noopener noreferrer" className="block p-6 text-sm font-bold text-[#0A1128]">Instagram</a>
      </blockquote>
    </div>
  );
};

export const InstagramEmbeds = ({ urls }) => {
  const [ref, seen] = useInView({ threshold: 0.05 });
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!seen) return;
    loadScript().then((ig) => setTimeout(() => ig.Embeds.process(), 0)).catch(() => setFailed(true));
  }, [seen, urls]);
  return (
    <section ref={ref} className="ig-section mt-12" data-testid="case-instagram">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0A1128]/60 mb-4"><Instagram className="h-4 w-4 text-[#E8173A]" />Instagram</div>
      <div className="grid gap-5 grid-cols-[minmax(0,1fr)] sm:grid-cols-2 lg:grid-cols-3 items-start">
        {seen && urls.map((u, i) => <Embed key={u} url={u} i={i} failed={failed} />)}
      </div>
    </section>
  );
};
